import { z } from 'zod';

const identifierSchema = z.string().trim().min(1).max(200);
const timestampSchema = z.string().datetime({ offset: true });
const tenantIdSchema = z.string().uuid();
const moneySchema = z.number().finite().nonnegative().max(100_000_000);

export const resourceCategorySchema = z.enum([
  'facilities',
  'vehicles',
  'equipment',
  'personnel',
  'training_learning',
  'assessment',
  'student_support',
  'digital_licensing',
  'documents',
  'other',
]);
export type ResourceCategory = z.infer<typeof resourceCategorySchema>;

export const resourceTypes = {
  facilities: [
    'classroom_training_room',
    'assessment_room',
    'workshop_practical_area',
    'laboratory_technical_room',
    'simulation_facility',
    'computer_laboratory',
    'library_learning_centre',
    'student_support_consultation_room',
    'amenity_common_area',
    'storage_preparation_area',
  ],
  vehicles: [
    'passenger_vehicle',
    'light_commercial_vehicle',
    'heavy_vehicle',
    'bus_coach',
    'motorcycle',
    'trailer_towed_unit',
    'marine_vessel',
    'mobile_training_unit',
    'emergency_special_purpose_vehicle',
  ],
  equipment: [
    'plant_machinery',
    'tools',
    'instruments_testing_equipment',
    'it_hardware',
    'communications_av_equipment',
    'simulation_equipment_training_aids',
    'furniture_ergonomic_equipment',
    'safety_emergency_equipment',
    'ppe',
    'assistive_adaptive_equipment',
  ],
  personnel: [
    'trainer',
    'assessor',
    'trainer_assessor',
    'facilitator_tutor',
    'subject_matter_industry_expert',
    'guest_presenter',
    'workplace_supervisor',
    'student_support_practitioner',
    'specialist_support_provider',
    'technical_support_personnel',
  ],
  training_learning: [
    'training_assessment_strategy',
    'training_delivery_plan',
    'session_lesson_plan',
    'trainer_facilitator_guide',
    'learner_guide_workbook',
    'presentation_media_resource',
    'learning_activity_exercise',
    'case_study_scenario',
    'demonstration_practical_resource',
    'reference_support_material',
  ],
  assessment: [
    'assessment_plan',
    'knowledge_assessment',
    'practical_observation_assessment',
    'project_product_assessment',
    'case_study_scenario_assessment',
    'workplace_evidence_tool',
    'other_party_evidence_tool',
    'rpl_assessment_tool',
    'candidate_instructions',
    'assessor_guide_benchmark',
  ],
  student_support: [
    'llnd_study_support',
    'tutoring_mentoring',
    'disability_accessibility_support',
    'counselling_wellbeing_support',
    'financial_hardship_support',
    'career_pathway_support',
    'digital_literacy_technology_support',
    'interpretation_captioning_notetaking',
    'external_referral_specialist_service',
    'student_support_information',
  ],
  digital_licensing: [
    'learning_management_system',
    'student_management_system',
    'online_assessment_platform',
    'virtual_classroom_webinar_platform',
    'industry_simulation_software',
    'authoring_content_software',
    'digital_library_content_subscription',
    'cloud_collaboration_platform',
    'website_domain_hosting',
    'copyright_digital_content_licence',
  ],
  documents: [
    'policy',
    'procedure',
    'work_instruction',
    'framework',
    'strategy',
    'plan_program',
    'guideline_manual_handbook',
    'form_template',
    'checklist',
    'agreement_terms_of_reference',
  ],
  other: [
    'consumable',
    'chemical_hazardous_material',
    'disposable_ppe',
    'workplace_material',
    'student_supplied_resource',
    'uniform_required_clothing',
    'hired_borrowed_resource',
    'external_service',
    'other_resource',
  ],
} as const;

const resourceTypeValues = Object.values(resourceTypes).flatMap((items) => items);
export const resourceTypeSchema = z.enum(resourceTypeValues as [string, ...string[]]);
export type ResourceType = z.infer<typeof resourceTypeSchema>;

export const resourceLifecycleSchema = z.enum([
  'draft',
  'active',
  'suspended',
  'retired',
  'archived',
]);
export type ResourceLifecycle = z.infer<typeof resourceLifecycleSchema>;
export const resourceAvailabilitySchema = z.enum([
  'available',
  'temporarily_unavailable',
  'restricted',
  'under_maintenance',
  'out_of_service',
  'withdrawn',
]);
export type ResourceAvailability = z.infer<typeof resourceAvailabilitySchema>;
export const resourceAssuranceStatusSchema = z.enum([
  'unverified',
  'under_review',
  'approved',
  'approved_with_conditions',
  'rejected',
  'approval_expired',
  'superseded',
]);
export type ResourceAssuranceStatus = z.infer<typeof resourceAssuranceStatusSchema>;
export const resourceProvisionSchema = z.enum([
  'rto_owned',
  'purchased',
  'leased',
  'hired',
  'licensed',
  'other_party_provided',
  'employer_provided',
  'work_placement_provider_supplied',
  'student_supplied',
  'other',
]);
export type ResourceProvision = z.infer<typeof resourceProvisionSchema>;

export const resourceVersionStatusSchema = z.enum([
  'draft',
  'under_review',
  'approved',
  'superseded',
  'withdrawn',
]);
export type ResourceVersionStatus = z.infer<typeof resourceVersionStatusSchema>;
export const resourceReviewCriterionSchema = z.enum([
  'fit_for_purpose',
  'current',
  'complete',
  'safe',
  'accessible',
  'sufficient',
  'available',
  'industry_relevant',
  'training_product_aligned',
  'licensed_authorised',
]);
export const resourceReviewResponseSchema = z.enum(['yes', 'no', 'partially', 'not_applicable']);
export const resourceReviewReasonSchema = z.enum([
  'scheduled_periodic',
  'new_resource_verification',
  'new_training_product',
  'new_release',
  'location_change',
  'delivery_mode_change',
  'incident_triggered',
  'complaint_triggered',
  'industry_change',
  'major_version',
  'other_party_arrangement',
  'ad_hoc',
  'personnel_change',
  'approval_expiry',
  'maintenance_overdue',
  'resource_unavailable',
]);
export const resourceApprovalDecisionSchema = z.enum([
  'approved',
  'approved_with_conditions',
  'rejected',
]);
export const resourceMaintenanceTypeSchema = z.enum([
  'inspection',
  'servicing',
  'calibration',
  'registration',
  'insurance',
  'certification',
  'warranty_review',
  'safety_check',
  'scheduled_maintenance',
  'ad_hoc_repair',
]);
export const resourceKeyDateTypeSchema = z.enum([
  'review_due',
  'approval_expiry',
  'maintenance_due',
  'inspection_due',
  'registration_expiry',
  'insurance_expiry',
  'licence_renewal',
  'subscription_renewal',
  'warranty_expiry',
  'agreement_expiry',
  'credential_expiry',
  'document_review',
  'version_review',
]);
export const resourceIssueSeveritySchema = z.enum(['low', 'medium', 'high', 'critical']);
export const resourceIssueStatusSchema = z.enum(['open', 'in_progress', 'resolved', 'closed']);
export const resourceRelationshipTypeSchema = z.enum([
  'located_in',
  'contains',
  'requires',
  'used_with',
  'supported_by',
  'replaces',
  'supersedes',
  'alternative_to',
]);

const locationIdsSchema = z.array(z.string().uuid()).max(100).default([]);
const tagSchema = z.string().trim().min(1).max(80);

export const resourceLocationSchema = z.object({
  locationId: z.string().uuid(),
  role: z.enum([
    'primary',
    'permitted',
    'home_base',
    'current_assignment',
    'organisation_wide',
    'remote_online',
  ]),
});
export type ResourceLocation = z.infer<typeof resourceLocationSchema>;

export const resourceTrainingProductSchema = z.object({
  id: identifierSchema,
  productCode: z.string().trim().min(1).max(64),
  title: z.string().trim().min(1).max(500),
  productType: z.string().trim().min(1).max(120),
  releaseNumber: z.string().trim().min(1).max(80),
  releaseIdentifier: z.string().trim().min(1).max(160),
  relationship: z.string().trim().min(1).max(160),
  locationIds: locationIdsSchema,
  deliveryModes: z.array(z.string().trim().min(1).max(80)).max(20).default([]),
  allocatedAt: timestampSchema,
  allocatedBy: identifierSchema,
  assuranceStatus: resourceAssuranceStatusSchema,
  reviewDate: timestampSchema.optional(),
  reviewedBy: identifierSchema.optional(),
  reviewedAt: timestampSchema.optional(),
  reviewNotes: z.string().trim().max(4_000).optional(),
  notes: z.string().trim().max(4_000).optional(),
  previousRelease: z.boolean().default(false),
  reviewRequired: z.boolean().default(false),
  unitMappings: z
    .array(
      z.object({
        unitCode: z.string().trim().min(1).max(64),
        requirementGroup: z.enum([
          'unit_requirement',
          'performance_evidence',
          'knowledge_evidence',
          'assessment_condition',
          'other',
        ]),
        requirementReference: z.string().trim().min(1).max(500).optional(),
        notes: z.string().trim().max(2_000).optional(),
      }),
    )
    .max(200)
    .default([]),
});
export type ResourceTrainingProduct = z.infer<typeof resourceTrainingProductSchema>;

export const resourceFileSchema = z.object({
  id: identifierSchema,
  fileId: identifierSchema,
  fileName: z.string().trim().min(1).max(300),
  contentType: z.string().trim().min(1).max(160),
  size: z.number().int().nonnegative(),
  checksum: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2_000).optional(),
  visibility: z.enum(['organisation', 'restricted']).default('organisation'),
  versionId: identifierSchema.optional(),
  uploadedAt: timestampSchema,
  uploadedBy: identifierSchema,
});
export type ResourceFile = z.infer<typeof resourceFileSchema>;

export const resourceVersionSchema = z.object({
  id: identifierSchema,
  versionNumber: z.string().trim().min(1).max(80),
  title: z.string().trim().min(1).max(300),
  status: resourceVersionStatusSchema,
  createdAt: timestampSchema,
  createdBy: identifierSchema,
  changeSummary: z.string().trim().min(1).max(4_000),
  fileIds: z.array(identifierSchema).max(100).default([]),
  reviewerId: identifierSchema.optional(),
  reviewStatus: resourceAssuranceStatusSchema.optional(),
  approvedBy: identifierSchema.optional(),
  approvedAt: timestampSchema.optional(),
  effectiveAt: timestampSchema.optional(),
  reviewDueAt: timestampSchema.optional(),
  supersededAt: timestampSchema.optional(),
  supersededByVersionId: identifierSchema.optional(),
  withdrawnAt: timestampSchema.optional(),
  withdrawalReason: z.string().trim().max(2_000).optional(),
});
export type ResourceVersion = z.infer<typeof resourceVersionSchema>;

export const resourceApprovalSchema = z.object({
  id: identifierSchema,
  submittedBy: identifierSchema,
  submittedAt: timestampSchema,
  reviewerId: identifierSchema.optional(),
  reviewerComments: z.string().trim().max(4_000).optional(),
  approverId: identifierSchema.optional(),
  decision: resourceApprovalDecisionSchema.optional(),
  decidedAt: timestampSchema.optional(),
  effectiveAt: timestampSchema.optional(),
  expiresAt: timestampSchema.optional(),
  conditions: z.string().trim().max(4_000).optional(),
  evidenceFileIds: z.array(identifierSchema).max(100).default([]),
});
export type ResourceApproval = z.infer<typeof resourceApprovalSchema>;

export const resourceReviewSchema = z.object({
  id: identifierSchema,
  reason: resourceReviewReasonSchema,
  status: z.enum(['scheduled', 'in_progress', 'complete', 'cancelled']),
  assignedReviewerId: identifierSchema.optional(),
  completedBy: identifierSchema.optional(),
  startedAt: timestampSchema.optional(),
  completedAt: timestampSchema.optional(),
  findings: z.string().trim().max(6_000).optional(),
  decision: z
    .enum(['suitable', 'suitable_with_actions', 'not_suitable', 'review_required'])
    .optional(),
  actionsRequired: z.string().trim().max(6_000).optional(),
  nextReviewAt: timestampSchema.optional(),
  evidenceFileIds: z.array(identifierSchema).max(100).default([]),
  responses: z
    .array(
      z.object({
        criterion: resourceReviewCriterionSchema,
        response: resourceReviewResponseSchema,
        comments: z.string().trim().max(2_000).optional(),
        finding: z.string().trim().max(2_000).optional(),
        requiredAction: z.string().trim().max(2_000).optional(),
      }),
    )
    .max(20)
    .default([]),
  createdAt: timestampSchema,
  createdBy: identifierSchema,
});
export type ResourceReview = z.infer<typeof resourceReviewSchema>;

export const resourceMaintenanceScheduleSchema = z.object({
  id: identifierSchema,
  maintenanceType: resourceMaintenanceTypeSchema,
  frequencyDays: z.number().int().positive().max(3_650).optional(),
  lastCompletedAt: timestampSchema.optional(),
  nextDueAt: timestampSchema,
  responsiblePersonId: identifierSchema.optional(),
  supplier: z.string().trim().max(300).optional(),
  estimatedCost: moneySchema.optional(),
  instructions: z.string().trim().max(4_000).optional(),
  evidenceRequired: z.boolean().default(false),
  status: z.enum(['active', 'paused', 'complete']).default('active'),
});
export type ResourceMaintenanceSchedule = z.infer<typeof resourceMaintenanceScheduleSchema>;

export const resourceMaintenanceEventSchema = z.object({
  id: identifierSchema,
  scheduleId: identifierSchema.optional(),
  maintenanceType: resourceMaintenanceTypeSchema,
  scheduledAt: timestampSchema.optional(),
  completedAt: timestampSchema.optional(),
  completedBy: identifierSchema.optional(),
  outcome: z.enum(['passed', 'passed_with_actions', 'failed', 'not_completed']),
  issuesIdentified: z.string().trim().max(4_000).optional(),
  cost: moneySchema.optional(),
  evidenceFileIds: z.array(identifierSchema).max(100).default([]),
  nextDueAt: timestampSchema.optional(),
  createdAt: timestampSchema,
  createdBy: identifierSchema,
});
export type ResourceMaintenanceEvent = z.infer<typeof resourceMaintenanceEventSchema>;

export const resourceKeyDateSchema = z.object({
  id: identifierSchema,
  type: resourceKeyDateTypeSchema,
  dueAt: timestampSchema,
  responsiblePersonId: identifierSchema.optional(),
  escalationPersonId: identifierSchema.optional(),
  reminderDays: z.array(z.number().int().min(0).max(365)).max(10).default([30, 14, 7, 0]),
  completedAt: timestampSchema.optional(),
  completedBy: identifierSchema.optional(),
  lastReminderAt: timestampSchema.optional(),
  lastReminderStageDays: z.number().int().min(0).max(365).optional(),
});
export type ResourceKeyDate = z.infer<typeof resourceKeyDateSchema>;

export const resourceRelationshipSchema = z.object({
  id: identifierSchema,
  relatedResourceId: identifierSchema,
  relationship: resourceRelationshipTypeSchema,
  notes: z.string().trim().max(2_000).optional(),
  createdAt: timestampSchema,
  createdBy: identifierSchema,
});
export type ResourceRelationship = z.infer<typeof resourceRelationshipSchema>;

export const resourceIssueSchema = z.object({
  id: identifierSchema,
  title: z.string().trim().min(1).max(300),
  description: z.string().trim().min(1).max(6_000),
  identifiedAt: timestampSchema,
  severity: resourceIssueSeveritySchema,
  source: z.string().trim().min(1).max(300),
  immediateAction: z.string().trim().max(4_000).optional(),
  responsiblePersonId: identifierSchema.optional(),
  dueAt: timestampSchema.optional(),
  status: resourceIssueStatusSchema,
  qualityActionId: identifierSchema.optional(),
  createdBy: identifierSchema,
});
export type ResourceIssue = z.infer<typeof resourceIssueSchema>;

const facilityDetailsSchema = z
  .object({
    organisationLocationId: z.string().uuid(),
    identifier: z.string().trim().max(160).optional(),
    floorOrArea: z.string().trim().max(160).optional(),
    capacity: z.number().int().nonnegative().max(100_000).optional(),
    maximumStudentCapacity: z.number().int().nonnegative().max(100_000).optional(),
    accessibility: z.string().trim().max(2_000).optional(),
    amenities: z.array(z.string().trim().min(1).max(160)).max(50).default([]),
    deliveryModes: z.array(z.string().trim().min(1).max(80)).max(20).default([]),
    safetyInformation: z.string().trim().max(4_000).optional(),
    emergencyInformation: z.string().trim().max(4_000).optional(),
    operatingHours: z.string().trim().max(500).optional(),
    accessRestrictions: z.string().trim().max(2_000).optional(),
  })
  .passthrough();
const vehicleDetailsSchema = z
  .object({
    registrationNumber: z.string().trim().max(100).optional(),
    vin: z.string().trim().max(100).optional(),
    make: z.string().trim().max(120).optional(),
    model: z.string().trim().max(120).optional(),
    year: z.number().int().min(1886).max(3000).optional(),
    odometer: z.number().nonnegative().max(10_000_000).optional(),
    capacity: z.number().int().nonnegative().max(100_000).optional(),
    licenceClassRequired: z.string().trim().max(100).optional(),
    baseLocationId: z.string().uuid().optional(),
    currentLocationId: z.string().uuid().optional(),
  })
  .passthrough();
const equipmentDetailsSchema = z
  .object({
    trackingMode: z.enum(['individual_asset', 'resource_pool']),
    serialNumber: z.string().trim().max(160).optional(),
    make: z.string().trim().max(120).optional(),
    model: z.string().trim().max(120).optional(),
    year: z.number().int().min(1886).max(3000).optional(),
    quantity: z.number().int().positive().max(1_000_000).default(1),
    availableQuantity: z.number().int().nonnegative().max(1_000_000).optional(),
    minimumRequiredQuantity: z.number().int().nonnegative().max(1_000_000).optional(),
    condition: z.string().trim().max(500).optional(),
    calibrationRequired: z.boolean().default(false),
  })
  .passthrough();
const personnelDetailsSchema = z
  .object({
    membershipUserId: identifierSchema,
    capacity: z.number().nonnegative().max(1_000_000).optional(),
    credentialStatus: z.string().trim().max(500).optional(),
    licences: z.array(z.string().trim().min(1).max(500)).max(100).default([]),
    industryCurrencyReviewAt: timestampSchema.optional(),
    capabilityReviewAt: timestampSchema.optional(),
  })
  .passthrough();
const trainingLearningDetailsSchema = z
  .object({
    format: z.string().trim().max(160).optional(),
    authorDeveloper: z.string().trim().max(300).optional(),
    copyrightHolder: z.string().trim().max(300).optional(),
    licence: z.string().trim().max(500).optional(),
    accessibilityReview: z.string().trim().max(2_000).optional(),
    mappingStatus: z.string().trim().max(160).optional(),
  })
  .passthrough();
const assessmentDetailsSchema = z
  .object({
    componentList: z.array(z.string().trim().min(1).max(500)).max(100).default([]),
    securityClassification: z.string().trim().max(160).optional(),
    triallingInformation: z.string().trim().max(4_000).optional(),
    validationHistory: z.string().trim().max(4_000).optional(),
    reasonableAdjustmentConsiderations: z.string().trim().max(4_000).optional(),
  })
  .passthrough();
const studentSupportDetailsSchema = z
  .object({
    serviceProvider: z.string().trim().max(300).optional(),
    internalExternal: z.enum(['internal', 'external']).optional(),
    onlineAvailable: z.boolean().default(false),
    eligibility: z.string().trim().max(2_000).optional(),
    accessPathway: z.string().trim().max(2_000).optional(),
    serviceHours: z.string().trim().max(500).optional(),
    contact: z.string().trim().max(500).optional(),
    studentCost: moneySchema.optional(),
    organisationCost: moneySchema.optional(),
  })
  .passthrough();
const digitalLicenceDetailsSchema = z
  .object({
    serviceName: z.string().trim().max(300).optional(),
    licenceType: z.string().trim().max(160).optional(),
    licenceNumber: z.string().trim().max(200).optional(),
    url: z.url().max(1_000).optional(),
    accountAdministratorId: identifierSchema.optional(),
    seats: z.number().int().nonnegative().max(1_000_000).optional(),
    allocatedSeats: z.number().int().nonnegative().max(1_000_000).optional(),
    autoRenewal: z.boolean().default(false),
    subscriptionCost: moneySchema.optional(),
    accessibilityReview: z.string().trim().max(2_000).optional(),
    privacySecurityReview: z.string().trim().max(2_000).optional(),
  })
  .passthrough();
const documentDetailsSchema = z
  .object({
    documentId: z.string().trim().max(160).optional(),
    organisationFunction: z.string().trim().max(160).optional(),
    distribution: z.string().trim().max(2_000).optional(),
    acknowledgementRequired: z.boolean().default(false),
  })
  .passthrough();
const otherDetailsSchema = z
  .object({ otherSubtype: z.string().trim().min(1).max(160).optional() })
  .passthrough();

export const resourceCategoryDetailsSchema = z.discriminatedUnion('category', [
  z.object({ category: z.literal('facilities'), details: facilityDetailsSchema }),
  z.object({ category: z.literal('vehicles'), details: vehicleDetailsSchema }),
  z.object({ category: z.literal('equipment'), details: equipmentDetailsSchema }),
  z.object({ category: z.literal('personnel'), details: personnelDetailsSchema }),
  z.object({ category: z.literal('training_learning'), details: trainingLearningDetailsSchema }),
  z.object({ category: z.literal('assessment'), details: assessmentDetailsSchema }),
  z.object({ category: z.literal('student_support'), details: studentSupportDetailsSchema }),
  z.object({ category: z.literal('digital_licensing'), details: digitalLicenceDetailsSchema }),
  z.object({ category: z.literal('documents'), details: documentDetailsSchema }),
  z.object({ category: z.literal('other'), details: otherDetailsSchema }),
]);
export type ResourceCategoryDetails = z.infer<typeof resourceCategoryDetailsSchema>;

const resourceBaseSchema = z.object({
  id: identifierSchema,
  tenantId: tenantIdSchema,
  resourceNumber: z.string().trim().min(1).max(100),
  name: z.string().trim().min(1).max(300),
  category: resourceCategorySchema,
  type: resourceTypeSchema,
  description: z.string().trim().max(8_000).optional(),
  internalReference: z.string().trim().max(200).optional(),
  assetNumber: z.string().trim().max(200).optional(),
  ownerId: identifierSchema.optional(),
  responsiblePersonId: identifierSchema.optional(),
  tags: z.array(tagSchema).max(50).default([]),
  notes: z.string().trim().max(8_000).optional(),
  provision: resourceProvisionSchema,
  supplier: z.string().trim().max(300).optional(),
  acquisitionDate: timestampSchema.optional(),
  purchaseDate: timestampSchema.optional(),
  agreementReference: z.string().trim().max(300).optional(),
  purchaseCost: moneySchema.optional(),
  replacementValue: moneySchema.optional(),
  lifecycle: resourceLifecycleSchema,
  availability: resourceAvailabilitySchema,
  assuranceStatus: resourceAssuranceStatusSchema,
  locations: z.array(resourceLocationSchema).max(100).default([]),
  categoryDetails: resourceCategoryDetailsSchema,
  trainingProducts: z.array(resourceTrainingProductSchema).max(500).default([]),
  files: z.array(resourceFileSchema).max(500).default([]),
  versions: z.array(resourceVersionSchema).max(100).default([]),
  approvals: z.array(resourceApprovalSchema).max(100).default([]),
  reviews: z.array(resourceReviewSchema).max(200).default([]),
  maintenanceSchedules: z.array(resourceMaintenanceScheduleSchema).max(100).default([]),
  maintenanceEvents: z.array(resourceMaintenanceEventSchema).max(500).default([]),
  keyDates: z.array(resourceKeyDateSchema).max(200).default([]),
  relationships: z.array(resourceRelationshipSchema).max(200).default([]),
  issues: z.array(resourceIssueSchema).max(200).default([]),
  createdBy: identifierSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const resourceSchema = resourceBaseSchema.superRefine((resource, context) => {
  if (!resourceTypes[resource.category].includes(resource.type as never)) {
    context.addIssue({
      code: 'custom',
      path: ['type'],
      message: 'Resource type does not match its category',
    });
  }
  if (resource.categoryDetails.category !== resource.category) {
    context.addIssue({
      code: 'custom',
      path: ['categoryDetails'],
      message: 'Category details do not match resource category',
    });
  }
  if (resource.category === 'facilities') {
    const facility = resource.categoryDetails.details as z.infer<typeof facilityDetailsSchema>;
    if (
      resource.locations.length !== 1 ||
      resource.locations[0]?.locationId !== facility.organisationLocationId
    ) {
      context.addIssue({
        code: 'custom',
        path: ['locations'],
        message: 'A facility must reference exactly one organisation location',
      });
    }
  }
  if (
    resource.category === 'other' &&
    resource.type === 'other_resource' &&
    !resource.categoryDetails.details.otherSubtype
  ) {
    context.addIssue({
      code: 'custom',
      path: ['categoryDetails', 'details', 'otherSubtype'],
      message: 'Other Resource requires a free-text subtype',
    });
  }
  const approvedVersions = resource.versions.filter((version) => version.status === 'approved');
  if (approvedVersions.length > 1) {
    context.addIssue({
      code: 'custom',
      path: ['versions'],
      message: 'Only one version can be currently approved',
    });
  }
});
export type Resource = z.infer<typeof resourceSchema>;

const resourceEditableFields = resourceBaseSchema.pick({
  name: true,
  category: true,
  type: true,
  description: true,
  internalReference: true,
  assetNumber: true,
  ownerId: true,
  responsiblePersonId: true,
  tags: true,
  notes: true,
  provision: true,
  supplier: true,
  acquisitionDate: true,
  purchaseDate: true,
  agreementReference: true,
  purchaseCost: true,
  replacementValue: true,
  lifecycle: true,
  availability: true,
  assuranceStatus: true,
  locations: true,
  categoryDetails: true,
  trainingProducts: true,
  keyDates: true,
  relationships: true,
});

export const createResourceSchema = resourceEditableFields
  .extend({
    name: resourceBaseSchema.shape.name,
    category: resourceCategorySchema,
    type: resourceTypeSchema,
    provision: resourceProvisionSchema.default('rto_owned'),
    locations: z.array(resourceLocationSchema).max(100).default([]),
    categoryDetails: resourceCategoryDetailsSchema,
  })
  .partial({
    description: true,
    internalReference: true,
    assetNumber: true,
    ownerId: true,
    responsiblePersonId: true,
    tags: true,
    notes: true,
    supplier: true,
    acquisitionDate: true,
    purchaseDate: true,
    agreementReference: true,
    purchaseCost: true,
    replacementValue: true,
    lifecycle: true,
    availability: true,
    assuranceStatus: true,
    trainingProducts: true,
    keyDates: true,
    relationships: true,
  })
  .strict();
export type CreateResource = z.infer<typeof createResourceSchema>;
// Training-product mappings have their own canonical scope and approval
// workflow. They must never be replaced through the generic Resource PATCH.
export const updateResourceSchema = resourceEditableFields
  .omit({ trainingProducts: true })
  .partial()
  .strict();
export type UpdateResource = z.infer<typeof updateResourceSchema>;

export const createResourceVersionSchema = resourceVersionSchema
  .pick({
    versionNumber: true,
    title: true,
    changeSummary: true,
    fileIds: true,
    effectiveAt: true,
    reviewDueAt: true,
  })
  .strict();
export const withdrawResourceVersionSchema = z
  .object({
    withdrawalReason: z.string().trim().min(3).max(2_000),
  })
  .strict();
export const submitResourceForReviewSchema = z
  .object({
    reviewerId: identifierSchema.optional(),
    evidenceFileIds: z.array(identifierSchema).max(100).default([]),
  })
  .strict();
export const decideResourceApprovalSchema = z
  .object({
    decision: resourceApprovalDecisionSchema,
    reviewerComments: z.string().trim().max(4_000).optional(),
    effectiveAt: timestampSchema.optional(),
    expiresAt: timestampSchema.optional(),
    conditions: z.string().trim().max(4_000).optional(),
    evidenceFileIds: z.array(identifierSchema).max(100).default([]),
  })
  .strict();
export const createResourceReviewSchema = resourceReviewSchema
  .pick({
    reason: true,
    assignedReviewerId: true,
    findings: true,
    decision: true,
    actionsRequired: true,
    nextReviewAt: true,
    evidenceFileIds: true,
    responses: true,
  })
  .extend({ status: resourceReviewSchema.shape.status.default('scheduled') })
  .strict();
export const completeResourceReviewSchema = createResourceReviewSchema
  .partial()
  .extend({
    status: z.literal('complete'),
  })
  .strict();
export const createResourceMaintenanceScheduleSchema = resourceMaintenanceScheduleSchema
  .omit({ id: true })
  .strict();
export const createResourceMaintenanceEventSchema = resourceMaintenanceEventSchema
  .omit({
    id: true,
    createdAt: true,
    createdBy: true,
  })
  .strict();
export const createResourceKeyDateSchema = resourceKeyDateSchema
  .omit({
    id: true,
    completedAt: true,
    completedBy: true,
    lastReminderAt: true,
    lastReminderStageDays: true,
  })
  .strict();
export const completeResourceKeyDateSchema = z
  .object({ completedAt: timestampSchema.optional() })
  .strict();
export const createResourceIssueSchema = resourceIssueSchema
  .omit({ id: true, createdBy: true, identifiedAt: true })
  .extend({
    identifiedAt: timestampSchema.optional(),
  })
  .strict();
export const createResourceRelationshipSchema = resourceRelationshipSchema
  .omit({
    id: true,
    createdAt: true,
    createdBy: true,
  })
  .strict();
export const createResourceFileSchema = resourceFileSchema
  .pick({
    fileId: true,
    description: true,
    visibility: true,
    versionId: true,
  })
  .strict();
export const createResourceMappingSchema = resourceTrainingProductSchema
  .omit({
    id: true,
    allocatedAt: true,
    allocatedBy: true,
    reviewedBy: true,
    reviewedAt: true,
    reviewNotes: true,
    previousRelease: true,
    reviewRequired: true,
  })
  .strict();
export const decideResourceMappingSchema = z
  .object({
    decision: resourceApprovalDecisionSchema,
    reviewNotes: z.string().trim().max(4_000).optional(),
  })
  .strict();

const resourceBulkIdsSchema = z
  .array(identifierSchema)
  .min(1)
  .max(100)
  .superRefine((ids, context) => {
    if (new Set(ids).size !== ids.length) {
      context.addIssue({
        code: 'custom',
        message: 'A Resource can only appear once in a bulk operation',
      });
    }
  });

/**
 * Deliberately omits approval. Bulk approval would bypass the individual review,
 * evidence and separation-of-duties checks in the approval workflow.
 */
export const resourceBulkOperationSchema = z.discriminatedUnion('operation', [
  z
    .object({
      operation: z.literal('assign_locations'),
      resourceIds: resourceBulkIdsSchema,
      locations: z.array(resourceLocationSchema).max(100),
    })
    .strict(),
  z
    .object({
      operation: z.literal('assign_owner'),
      resourceIds: resourceBulkIdsSchema,
      ownerId: identifierSchema.optional(),
      responsiblePersonId: identifierSchema.optional(),
    })
    .strict(),
  z
    .object({
      operation: z.literal('map_training_product'),
      resourceIds: resourceBulkIdsSchema,
      mapping: createResourceMappingSchema,
    })
    .strict(),
  z
    .object({
      operation: z.literal('update_review_date'),
      resourceIds: resourceBulkIdsSchema,
      nextReviewAt: timestampSchema,
    })
    .strict(),
  z
    .object({
      operation: z.literal('archive'),
      resourceIds: resourceBulkIdsSchema,
    })
    .strict(),
]);
export type ResourceBulkOperation = z.infer<typeof resourceBulkOperationSchema>;

export const importResourcesSchema = z
  .object({
    items: z.array(createResourceSchema).min(1).max(100),
  })
  .strict();
export type ImportResources = z.infer<typeof importResourcesSchema>;

export const previewResourceImportSchema = z
  .object({
    items: z.array(z.unknown()).min(1).max(100),
  })
  .strict();

export const resourceSavedViewFiltersSchema = z
  .object({
    search: z.string().trim().max(300).optional(),
    category: resourceCategorySchema.optional(),
    type: resourceTypeSchema.optional(),
    lifecycle: resourceLifecycleSchema.optional(),
    availability: resourceAvailabilitySchema.optional(),
    assuranceStatus: resourceAssuranceStatusSchema.optional(),
    locationId: z.string().uuid().optional(),
    productCode: z.string().trim().max(64).optional(),
    releaseIdentifier: z.string().trim().max(160).optional(),
    ownerId: identifierSchema.optional(),
    responsiblePersonId: identifierSchema.optional(),
    alertsOnly: z.boolean().optional(),
  })
  .strict();
export type ResourceSavedViewFilters = z.infer<typeof resourceSavedViewFiltersSchema>;

export const resourceReportTypeSchema = z.enum([
  'complete_register',
  'category_register',
  'location_register',
  'training_product_register',
  'training_product_matrix',
  'review_schedule',
  'maintenance_schedule',
  'expiry_register',
  'unverified_resources',
  'awaiting_approval',
  'approval_history',
  'document_register',
  'training_learning_register',
  'assessment_register',
  'digital_licence_register',
  'vehicle_register',
  'equipment_asset_register',
  'personnel_allocation',
]);
export type ResourceReportType = z.infer<typeof resourceReportTypeSchema>;

export const resourceSavedViewSchema = z.object({
  id: identifierSchema,
  name: z.string().trim().min(1).max(160),
  reportType: resourceReportTypeSchema.default('complete_register'),
  filters: resourceSavedViewFiltersSchema.default({}),
  createdBy: identifierSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type ResourceSavedView = z.infer<typeof resourceSavedViewSchema>;
export const createResourceSavedViewSchema = resourceSavedViewSchema
  .pick({
    name: true,
    reportType: true,
    filters: true,
  })
  .strict();
export const updateResourceSavedViewSchema = createResourceSavedViewSchema
  .partial()
  .strict()
  .refine(
    (value) => Object.keys(value).length > 0,
    'At least one saved view field must be updated',
  );

export const resourceListQuerySchema = z
  .object({
    page: z.coerce.number().int().positive().max(10_000).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    search: z.string().trim().max(300).optional(),
    category: resourceCategorySchema.optional(),
    type: resourceTypeSchema.optional(),
    lifecycle: resourceLifecycleSchema.optional(),
    availability: resourceAvailabilitySchema.optional(),
    assuranceStatus: resourceAssuranceStatusSchema.optional(),
    locationId: z.string().uuid().optional(),
    productCode: z.string().trim().max(64).optional(),
    releaseIdentifier: z.string().trim().max(160).optional(),
    ownerId: identifierSchema.optional(),
    responsiblePersonId: identifierSchema.optional(),
    alertsOnly: z.enum(['true', 'false']).optional(),
    reportType: resourceReportTypeSchema.optional(),
  })
  .strict();
export type ResourceListQuery = z.infer<typeof resourceListQuerySchema>;
